import { Box, Table, TableContainer, Tbody, Th, Thead, Tr } from '@chakra-ui/react';
import { useEffect, useState, type ReactNode } from 'react';
import { useSelector } from 'react-redux';

import DeltaTriangleIcon from 'components/Icons/DeltaTriangleIcon';
import {
  getDashboardBlocks,
  getSelectedDashboardScenario,
  getCompareDashboardScenario,
  resolveIndicatorDimension,
} from 'redux/DashboardSliceV2';

import VarianceRow from './VarianceRows';

export interface VarianceProperties {
  output: any;
  columnSettings: any;
  columnPositions?: any;
  isExport?: boolean;
}

// isExport stays in the props type for callers (ExportTable.tsx still passes
// it), but the component itself no longer branches on it — the delta-triangle
// swap it used to gate now always applies (see renderColumnHeader below).
const Variance = ({ output, columnSettings, columnPositions }: VarianceProperties) => {
  const [columns, setColumns] = useState<any>([]);
  const [rows, setRows] = useState<any>([]);
  const blockDetails = useSelector(getDashboardBlocks);
  const selectedDashboardScenario = useSelector(getSelectedDashboardScenario);
  const compareDashboardScenario = useSelector(getCompareDashboardScenario);

  const renderColumnHeader = (columnName: string): ReactNode => {
    if (columnName === 'Indicators') return '';

    // The underlying column key keeps its literal ▲ prefix (formatRowData below
    // keys off it), but on screen that's a raw Unicode glyph — no way to size,
    // colour or align it consistently, which is why it read as oversized/crude
    // next to the rest of the header's small, muted type. Same SVG swap now
    // runs on screen too, not just in export (where it started, working around
    // Kendo's PDF font embedding dropping the glyph — same fix, two reasons).
    if (columnName.startsWith('▲')) {
      const remainder = columnName.slice(1);
      return (
        <Box as='span' display='inline-flex' alignItems='center' gap='3px'>
          <DeltaTriangleIcon width='7px' height='7px' color='#8B5CF6' />
          <Box as='span'>{remainder}</Box>
        </Box>
      );
    }

    if (columnName?.length > 20) return `${columnName.slice(0, 20)}...`;
    return columnName;
  };

  const getDimensionNameFromId = (
    dimensionId: number | string,
    blockId: number | string,
    indicatorId: number | string,
  ): string | null => {
    const found = resolveIndicatorDimension({
      blockDetails,
      blockId,
      indicatorId,
      dimensionId,
    });
    return found?.indicator_dimension_name ?? null;
  };

  const extractColumns = (settings: any): string[] => {
    // Build the list of columns in the order they are enabled in settings.
    // The baseline/actual column is always present — it is not user-toggleable.
    const columnList: string[] = ['Indicators'];
    if (settings?.actual_column_name) {
      columnList.push(settings.actual_column_name);
    } else if (selectedDashboardScenario?.name) {
      columnList.push(selectedDashboardScenario.name);
    } else {
      columnList.push('Actual');
    }

    if (settings?.previous_year) columnList.push('Prior Year');
    if (settings?.varience_to_py) columnList.push('▲PY');
    if (settings?.varience_percentage_py) columnList.push('▲PY%');

    // Every plan/comparison column is derived from a compare scenario, so none
    // of them can show anything meaningful once Compare is switched off in the
    // scenario picker (which clears the compare scenario). They used to render
    // regardless — falling back to a literal "Comparison (PL)" header over
    // empty cells — which is why turning Compare off appeared to do nothing.
    const hasCompareScenario = Boolean(compareDashboardScenario?.id);
    if (settings?.plan && hasCompareScenario) {
      if (settings?.plan_comparison_column_name) {
        columnList.push(settings?.plan_comparison_column_name);
      } else if (compareDashboardScenario?.name) {
        columnList.push(compareDashboardScenario.name);
      } else {
        columnList.push('Comparison (PL)');
      }
    }
    if (settings?.varience_to_pl && hasCompareScenario) columnList.push('▲PL');
    if (settings?.varience_percentage_pl && hasCompareScenario) columnList.push('▲PL%');

    const colPos: Record<string, number> | undefined = columnPositions;
    if (colPos && typeof colPos === 'object') {
      // Map display column names to their position keys used in columnPositions
      const getPositionKey = (columnName: string): string => {
        // Direct matches (variance columns already use the correct keys)
        if (
          columnName === '▲PY' ||
          columnName === '▲PY%' ||
          columnName === '▲PL' ||
          columnName === '▲PL%'
        ) {
          return columnName;
        }
        // Map display names to position keys
        if (columnName === 'Prior Year') return 'PY';
        const planComparisonName =
          settings?.plan_comparison_column_name ||
          compareDashboardScenario?.name ||
          'Comparison (PL)';
        if (columnName === 'Comparison (PL)' || columnName === planComparisonName) {
          return 'Comparison (PL)';
        }
        // Actual column - map to 'Actual' position key
        const actualColumnName =
          settings?.actual_column_name || selectedDashboardScenario?.name || 'Actual';
        if (columnName === 'Actual' || columnName === actualColumnName) {
          return 'Actual';
        }
        // Return original name (will default to position 999)
        return columnName;
      };

      const [first, ...rest] = columnList;
      const sortedRest = rest.slice().sort((a, b) => {
        const keyA = getPositionKey(a);
        const keyB = getPositionKey(b);
        const posA = colPos[keyA] ?? 999;
        const posB = colPos[keyB] ?? 999;
        return posA - posB;
      });
      return [first, ...sortedRest];
    }
    return columnList;
  };
  const formatRowData = (rawData: any[], settings: any) => {
    if (!rawData || !Array.isArray(rawData)) return [];
    const data = JSON.parse(JSON.stringify(rawData));
    return data
      ?.sort((a: any, b: any) => {
        const positionA = a?.indicator?.position ?? 0;
        const positionB = b?.indicator?.position ?? 0;
        return positionA - positionB;
      })
      ?.map((row: any) => {
        // variance_presentation lives inside total[0] (not at the row top level),
        // so capture it from there before the spread to avoid overwriting with {}.
        const variancePresentation = { ...row.total[0]?.variance_presentation };
        return {
          indicator: row.indicator,
          isCollapsable: row.dim_df?.length > 0,
          isCollapsed: true,
          ...row.total[0],
          indicator_id: row.indicator.indicator_id,
          Indicators: row.indicator?.indicator_name || row.indicator?.name,
          variance_presentation: variancePresentation,
          children:
            row.dim_df && row.dim_df.length > 0
              ? row.dim_df
                  .map((r: any) => {
                    const dimensionName =
                      getDimensionNameFromId(
                        row.indicator.dimension_id,
                        row.indicator.block_id,
                        row.indicator.indicator_id,
                      ) || row.indicator.dimension_name;
                    return {
                      ...r,
                      indicator: row.indicator,
                      Indicators: r[dimensionName] || r[`${row.indicator.dimension_name}`],
                    };
                  })
                  .map((r: any) => {
                    const formattedRow: any = { ...r };
                    const childVariancePresentation = {
                      ...r.variance_presentation,
                    };
                    delete formattedRow.dim_df;
                    if (settings?.varience_to_py) {
                      formattedRow['▲PY'] = r.PY;
                      delete formattedRow.PY;
                    }
                    if (settings?.varience_percentage_py) {
                      formattedRow['▲PY%'] = r['PY%'];
                      delete formattedRow['PY%'];
                      // Keep variance presentation tokens aligned with the display column names.
                      if (childVariancePresentation['PY%']) {
                        childVariancePresentation['▲PY%'] = childVariancePresentation['PY%'];
                        delete childVariancePresentation['PY%'];
                      }
                    }
                    if (settings?.varience_to_pl) {
                      formattedRow['▲PL'] = r.PL;
                      delete formattedRow.PL;
                    }
                    if (settings?.varience_percentage_pl) {
                      formattedRow['▲PL%'] = r['PL%'];
                      delete formattedRow['PL%'];
                      // Keep variance presentation tokens aligned with the display column names.
                      if (childVariancePresentation['PL%']) {
                        childVariancePresentation['▲PL%'] = childVariancePresentation['PL%'];
                        delete childVariancePresentation['PL%'];
                      }
                    }

                    const actualColumnName =
                      settings?.actual_column_name || selectedDashboardScenario?.name || 'Actual';
                    // API always returns the baseline under the key "Actual". Remap to the
                    // display column name, but never delete "Actual" when it IS the display name.
                    formattedRow[actualColumnName] = r[actualColumnName] ?? r.Actual;
                    if (actualColumnName !== 'Actual') {
                      delete formattedRow.Actual;
                    }
                    if (
                      settings?.actual_column_name &&
                      actualColumnName !== settings?.actual_column_name
                    ) {
                      delete formattedRow[settings?.actual_column_name];
                    }

                    const planComparisonColumnName =
                      settings?.plan_comparison_column_name ||
                      compareDashboardScenario?.name ||
                      'Comparison (PL)';
                    // If custom column name is not set, always use data from 'Comparison' column (API response)
                    // If custom column name is set, try that first, then fall back to 'Comparison'
                    if (settings?.plan_comparison_column_name) {
                      formattedRow[planComparisonColumnName] =
                        r[planComparisonColumnName] ?? r.Comparison;
                    } else {
                      formattedRow[planComparisonColumnName] = r.Comparison;
                    }
                    delete formattedRow['Comparison (PL)'];
                    delete formattedRow.Comparison;
                    if (
                      settings?.plan_comparison_column_name &&
                      planComparisonColumnName !== settings?.plan_comparison_column_name
                    ) {
                      delete formattedRow[settings?.plan_comparison_column_name];
                    }
                    formattedRow.variance_presentation = childVariancePresentation;
                    return formattedRow;
                  })
              : [],
        };
      })
      .map((row: any) => {
        const formattedRow: any = { ...row };
        const variancePresentation = { ...row.variance_presentation };
        delete formattedRow.dim_df;
        if (settings?.varience_to_py) {
          formattedRow['▲PY'] = row.PY;
          delete formattedRow.PY;
        }
        if (settings?.varience_percentage_py) {
          formattedRow['▲PY%'] = row['PY%'];
          delete formattedRow['PY%'];
          // Keep variance presentation tokens aligned with the display column names.
          if (variancePresentation['PY%']) {
            variancePresentation['▲PY%'] = variancePresentation['PY%'];
            delete variancePresentation['PY%'];
          }
        }
        if (settings?.varience_to_pl) {
          formattedRow['▲PL'] = row.PL;
          delete formattedRow.PL;
        }
        if (settings?.varience_percentage_pl) {
          formattedRow['▲PL%'] = row['PL%'];
          delete formattedRow['PL%'];
          // Keep variance presentation tokens aligned with the display column names.
          if (variancePresentation['PL%']) {
            variancePresentation['▲PL%'] = variancePresentation['PL%'];
            delete variancePresentation['PL%'];
          }
        }

        const actualColumnName =
          settings?.actual_column_name || selectedDashboardScenario?.name || 'Actual';
        // API always returns the baseline under the key "Actual". Remap to the
        // display column name, but never delete "Actual" when it IS the display name.
        formattedRow[actualColumnName] = row[actualColumnName] ?? row.Actual;
        if (actualColumnName !== 'Actual') {
          delete formattedRow.Actual;
        }
        if (settings?.actual_column_name && actualColumnName !== settings?.actual_column_name) {
          delete formattedRow[settings?.actual_column_name];
        }

        const planComparisonColumnName =
          settings?.plan_comparison_column_name ||
          compareDashboardScenario?.name ||
          'Comparison (PL)';
        // If custom column name is not set, always use data from 'Comparison' column (API response)
        // If custom column name is set, try that first, then fall back to 'Comparison'
        if (settings?.plan_comparison_column_name) {
          formattedRow[planComparisonColumnName] = row[planComparisonColumnName] ?? row.Comparison;
        } else {
          formattedRow[planComparisonColumnName] = row.Comparison;
        }
        delete formattedRow['Comparison (PL)'];
        delete formattedRow.Comparison;
        if (
          settings?.plan_comparison_column_name &&
          planComparisonColumnName !== settings?.plan_comparison_column_name
        ) {
          delete formattedRow[settings?.plan_comparison_column_name];
        }

        formattedRow.variance_presentation = variancePresentation;
        return formattedRow;
      });
  };

  useEffect(() => {
    if (columnSettings && output) {
      const extractedColumns = extractColumns(columnSettings);
      setColumns(extractedColumns);
      const formattedRows = formatRowData(output.data, columnSettings);
      setRows(formattedRows);
    }
  }, [columnSettings, output, selectedDashboardScenario, compareDashboardScenario, blockDetails]);

  // Chakra's polymorphic types can sometimes explode into "union too complex"
  // in TS when many responsive props are provided inline. Keep props in a plain object.
  const tableContainerProperties: any = {
    overflowY: 'unset',
    overflowX: 'unset',
    mb: { base: 4.75, md: 0 },
    mt: { base: 0, md: 0 },
    borderRadius: '10px',
    className: 'table_scroll indicator-table dashboard-drag-header',
    cursor: 'grab',
  };

  // Matches DashboardTable's scroll shell exactly — same radius, same hairline,
  // same border colour. Variance previously had no border at all, so the two
  // instrument types read as different components sitting on the same page.
  // maxH, not h — same reasoning as DashboardTable's shell: the border should
  // wrap the rows that exist rather than stretching to the card's full height
  // and leaving an empty bordered area under the last row.
  const outerBoxProperties: any = {
    borderRadius: '10px',
    overflowY: 'auto',
    overflowX: 'auto',
    className: 'scroll',
    maxH: '100%',
    border: '1px solid',
    borderColor: '#E4D5FC',
  };

  return (
    <Box {...outerBoxProperties}>
      <TableContainer {...tableContainerProperties}>
        {/* `dashboardV2`, not `base` — `base` paints its header bar green.800
            (the app-wide dark chrome), which is why a variance instrument and a
            table instrument sitting side by side on the same dashboard looked
            like two unrelated products. Same variant = same sticky behaviour,
            type ramp and gridlines as DashboardTable. */}
        {/* width:max-content + minWidth:100% — same as DashboardTable: Chakra's
            default width:100% meant extra comparison columns just squeezed
            narrower instead of overflowing, so the shell never scrolled. */}
        <Table variant='dashboardV2' width='max-content' minWidth='100%'>
          {/* Sticky header row — stays pinned to the top of the scroll container. */}
          <Thead height={'9'} position='sticky' top={0} zIndex={2}>
            <Tr>
              {columns.map((column: string, colIndex: number) => (
                <Th
                  key={column}
                  textAlign={'center'}
                  fontFamily={'regular_roboto'}
                  fontSize={'2xs'}
                  fontWeight={500}
                  textTransform='none'
                  title={column}
                  sx={{
                    backgroundColor: '#F5F0FF !important',
                    color: '#121826 !important',
                    borderRight: '1px solid #E4D5FC !important',
                  }}
                  // Freeze the Indicators (first) column header.
                  // zIndex=3 keeps this corner cell above both the sticky-top header
                  // siblings (zIndex=2) and the sticky-left body cells (zIndex=1).
                  {...(colIndex === 0 ? { position: 'sticky', left: 0, zIndex: 3 } : {})}
                >
                  {renderColumnHeader(column)}
                </Th>
              ))}
            </Tr>
          </Thead>
          <Tbody>
            {rows.map((row: any) => (
              <VarianceRow key={row.indicator_id} row={row} columns={columns} />
            ))}
          </Tbody>
        </Table>
      </TableContainer>
    </Box>
  );
};
export default Variance;
